import type { Region } from "@typedoc/contracts";
import { AppError, errorFromResponse, normalizeAppError } from "./app-error";
import { boundedRequest } from "./bounded-request";
import type { AccessScope, CachedAccessToken, RegionalTokenCache } from "./regional-token-cache";
import type { RpcFetch } from "./rpc/transport";

export type ApiBases = Partial<Record<Region, string>>;
export type RegionalOptions = {
  cache: RegionalTokenCache;
  apiBases: ApiBases;
  fetcher?: RpcFetch;
  timeoutMs?: number;
  /** The caller's own cancellation, such as a page that was left: it stops this request only. */
  signal?: AbortSignal;
};
/** Who is calling and how, bound once: a workspace's scope and the options of its regional calls. */
export type RegionalSession = { scope: AccessScope; options: RegionalOptions };
export type JsonResponse = Pick<Response, "ok" | "status" | "headers" | "json">;
export type RegionalSend = (
  base: string,
  request: { headers: { Authorization: string }; init: { signal: AbortSignal } },
  workspaceId: string,
) => Promise<JsonResponse>;

/**
 * One call to the workspace's regional API: the token's region picks the API, one retry on 401
 * with a fresh token, and every await rechecks that the scope is still current so a workspace
 * switch can never apply an old answer. `call` makes the request and reads the answer; it reports
 * a 401 by throwing an `AppError` with that status.
 */
export async function regionalCall<T>(
  scope: AccessScope,
  options: RegionalOptions,
  call: (
    base: string,
    request: Parameters<RegionalSend>[1],
    access: CachedAccessToken,
  ) => Promise<T>,
): Promise<T> {
  for (let attempt = 0; attempt < 2; attempt++) {
    if (options.signal?.aborted) throw normalizeAppError(undefined, { signal: options.signal });
    const access = await options.cache.get(scope);
    if (!options.cache.isCurrent(scope, access)) throw new AppError("ACCESS_CHANGED");
    const base = options.apiBases[access.claims.region];
    if (!base) throw new AppError("REGION_UNAVAILABLE");
    const outcome = await boundedRequest(
      async (signal) => {
        const request = { headers: { Authorization: `Bearer ${access.token}` }, init: { signal } };
        try {
          const value = await call(base, request, access);
          // The body took time to read; a workspace switch during it must not apply this answer.
          if (!options.cache.isCurrent(scope, access)) throw new AppError("ACCESS_CHANGED");
          return { retry: false as const, value };
        } catch (error) {
          if (!options.cache.isCurrent(scope, access)) throw new AppError("ACCESS_CHANGED");
          if (attempt === 0 && error instanceof AppError && error.status === 401) {
            return { retry: true as const };
          }
          throw error;
        }
      },
      options.signal ? AbortSignal.any([access.signal, options.signal]) : access.signal,
      options.timeoutMs,
    );
    if (!outcome.retry) return outcome.value;
    // Same scope, new token: siblings sharing the refused token reuse this reissue, not abort.
    options.cache.refresh(scope, access);
  }
  throw new AppError("UNAUTHORIZED");
}

/** `regionalCall` for a raw response; `read` turns it into the result, and may refuse it. */
export function regionalRequest<T>(
  scope: AccessScope,
  options: RegionalOptions,
  send: RegionalSend,
  read: (response: JsonResponse, access: CachedAccessToken) => Promise<T>,
): Promise<T> {
  return regionalCall(scope, options, async (base, request, access) => {
    // Hono substitutes path parameters literally; encode the opaque workspace ID once.
    const response = await send(base, request, encodeURIComponent(scope.workspaceId));
    if (!options.cache.isCurrent(scope, access)) throw new AppError("ACCESS_CHANGED");
    if (response.status === 401) throw await errorFromResponse(response);
    return read(response, access);
  });
}
